import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { Video, VideoOff, Mic, MicOff, Phone, Monitor, Users, Clock } from 'lucide-react';

export default function ConsultationRoom() {
  const { user } = useAuth();
  const [inRoom, setInRoom] = useState(false);
  const [videoOn, setVideoOn] = useState(true);
  const [micOn, setMicOn] = useState(true);

  const handleJoinRoom = () => {
    setInRoom(true);
  };

  const handleLeaveRoom = () => {
    setInRoom(false);
    setVideoOn(true);
    setMicOn(true);
  };

  if (!inRoom) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="consultation-lobby">
        <div>
          <h1 className="text-2xl font-bold">Sala de Atendimento</h1>
          <p className="text-muted-foreground">Teleconsulta por vídeo</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-blue-600" />
              Preparação para a Sessão
            </CardTitle>
            <CardDescription>
              Verifique sua câmera e microfone antes de entrar
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Preview area */}
            <div className="aspect-video bg-slate-900 rounded-lg flex items-center justify-center">
              <div className="text-center text-slate-400">
                <Monitor className="h-16 w-16 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Preview da câmera</p>
                <Badge variant="outline" className="mt-2 text-amber-500 border-amber-500">
                  Modo Demonstração (Mock)
                </Badge>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <Button
                variant={videoOn ? 'default' : 'destructive'}
                size="lg"
                className="rounded-full w-14 h-14"
                onClick={() => setVideoOn(!videoOn)}
                data-testid="toggle-video"
              >
                {videoOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
              </Button>
              <Button
                variant={micOn ? 'default' : 'destructive'}
                size="lg"
                className="rounded-full w-14 h-14"
                onClick={() => setMicOn(!micOn)}
                data-testid="toggle-mic"
              >
                {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
              </Button>
            </div>

            <Button
              onClick={handleJoinRoom}
              className="w-full bg-green-600 hover:bg-green-700"
              size="lg"
              data-testid="join-room-button"
            >
              <Video className="h-5 w-5 mr-2" />
              Entrar na Sala
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium text-sm">Sobre as sessões</p>
                <p className="text-sm text-muted-foreground">
                  As sessões de teleconsulta utilizam tecnologia de vídeo em tempo real. 
                  Certifique-se de estar em um ambiente tranquilo com boa conexão de internet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // In-room view
  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="consultation-room">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge className="bg-green-600">Ao Vivo</Badge>
          <span className="text-sm text-muted-foreground">Sala de Atendimento</span>
        </div>
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">1 participante</span>
        </div>
      </div>

      {/* Main video area */}
      <div className="aspect-video bg-slate-900 rounded-xl flex items-center justify-center relative">
        <div className="text-center text-slate-400">
          <Video className="h-20 w-20 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">Sessão em andamento</p>
          <Badge variant="outline" className="mt-2 text-amber-500 border-amber-500">
            Modo Demonstração (LiveKit Mock)
          </Badge>
          <p className="text-xs mt-2 text-slate-500">
            Quando conectado com chaves LiveKit reais, o vídeo aparecerá aqui
          </p>
        </div>

        {/* Self-view thumbnail */}
        <div className="absolute bottom-4 right-4 w-48 h-36 bg-slate-800 rounded-lg flex items-center justify-center border border-slate-700">
          <div className="text-center text-slate-500">
            <Users className="h-8 w-8 mx-auto" />
            <p className="text-xs mt-1">{user?.name || 'Você'}</p>
          </div>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center justify-center gap-4 py-4">
        <Button
          variant={micOn ? 'outline' : 'destructive'}
          size="lg"
          className="rounded-full w-14 h-14"
          onClick={() => setMicOn(!micOn)}
          data-testid="room-toggle-mic"
        >
          {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </Button>
        <Button
          variant={videoOn ? 'outline' : 'destructive'}
          size="lg"
          className="rounded-full w-14 h-14"
          onClick={() => setVideoOn(!videoOn)}
          data-testid="room-toggle-video"
        >
          {videoOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
        </Button>
        <Button
          variant="destructive"
          size="lg"
          className="rounded-full w-14 h-14"
          onClick={handleLeaveRoom}
          data-testid="leave-room-button"
        >
          <Phone className="h-5 w-5 rotate-[135deg]" />
        </Button>
      </div>
    </div>
  );
}
